import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export default function ConsoleDialog({
    show,
    onClose,
    title,
    description,
    icon = 'fa-cube',
    tone = 'default',
    children,
    footer,
    className = '',
    bodyClassName = '',
    closeLabel = 'Close',
}) {
    const dialogRef = useRef(null)
    const closeRef = useRef(null)
    const previousFocusRef = useRef(null)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    const requestClose = () => onCloseRef.current?.()

    useEffect(() => {
        if (!show) return undefined
        const previousOverflow = document.body.style.overflow
        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const focusDialogClose = () => closeRef.current?.focus()
        const closeOnKeydown = (event) => {
            if (event.key === 'Escape') onCloseRef.current?.()
            if (event.key !== 'Tab') return
            const focusable = Array.from(dialogRef.current?.querySelectorAll(
                'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ) || [])
            if (focusable.length === 0) return
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }
        document.body.style.overflow = 'hidden'
        focusDialogClose()
        document.addEventListener('keydown', closeOnKeydown)
        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', closeOnKeydown)
            previousFocusRef.current?.focus?.()
        }
    }, [show])

    if (!show || typeof document === 'undefined') return null
    return createPortal(
        <div className="console-dialog-layer" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) requestClose()
        }}>
            <section ref={dialogRef} className={`console-dialog is-${tone} ${className}`.trim()} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
                <header className="console-dialog__header">
                    <span className="console-dialog__icon"><i className={`fa ${icon}`} /></span>
                    <div><h2>{title}</h2>{description && <p>{description}</p>}</div>
                    <button ref={closeRef} type="button" className="console-dialog__close" onClick={requestClose} aria-label={closeLabel} title={closeLabel}><i className="fa fa-times" /></button>
                </header>
                <div className={`console-dialog__body ${bodyClassName}`.trim()}>{children}</div>
                {footer && <footer className="console-dialog__footer">{footer}</footer>}
            </section>
        </div>,
        document.body
    )
}
